import Api from '../../../../Services/Api';

/**
 * Generate ad copy using OpenAI GPT
 * @param {Object} params Parameters for text generation
 * @param {Object} params.page The selected RSOC page
 * @param {string} params.objective The campaign objective
 * @returns {Promise<Object>} Generated ad copy including headline, primary text, and description
 */
export const generateAdCopy = async (params) => {
  try {
    const response = await Api.generateRsocPageWithOpenAI({
      type: 'ad_copy',
      params: {
        title: params.page.title,
        format: 'facebook_ad',
      },
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to generate ad copy');
    }

    return response.data;
  } catch (error) {
    console.error('Error generating ad copy:', error);
    throw error;
  }
};

/**
 * Generate ad image using OpenAI DALL-E
 * @param {Object} params Parameters for image generation
 * @param {Object} params.page The selected RSOC page
 * @param {string} params.style Image style preference
 * @param {string} params.customPrompt Custom prompt to override the default (optional)
 * @returns {Promise<Object>} Generated image data including URL
 */
export const generateAdImage = async (params) => {
  try {
    const response = await Api.generateRsocPageWithOpenAI({
      type: 'ad_image',
      params: {
        title: params.page.title,
        format: 'facebook_ad',
        size: '1080x1080', // Facebook recommended size
        style: params.style || 'professional',
        slug: params.page.slug || 'default',
        custom_prompt: params.customPrompt || null, // Add custom prompt if provided
      },
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to generate ad image');
    }

    return response.data;
  } catch (error) {
    console.error('Error generating ad image:', error);
    throw error;
  }
};

/**
 * Format page content for optimal AI prompt
 * @param {Object} page RSOC page data
 * @returns {Object} Formatted content for AI generation
 */
export const formatPageForAI = (page) => {
  return {
    title: page.title,
    slug: page.slug || 'default',
  };
};

/**
 * Fetch pre-generated images for a specific page/slug
 * @param {string} slug The page slug to fetch images for
 * @param {boolean} selectDiverse Whether to return only 4 diverse images or all images (default: false for all images)
 * @returns {Promise<Array>} Array of image objects with URLs
 */
export const fetchPreloadedImages = async (slug, selectDiverse = false) => {
  try {
    console.log(`Attempting to fetch preloaded images for slug: "${slug}"`);
    
    // Request pre-generated images from our API endpoint via Api service
    const result = await Api.getRsocPageImages(slug);
    console.log('Preloaded images API response:', result);
    
    if (result.success && result.data && result.data.length > 0) {
      console.log(`Found ${result.data.length} preloaded images for slug: ${slug}`);
      
      // If not selecting diverse images, return all images
      if (!selectDiverse) {
        console.log(`Returning all ${result.data.length} images`);
        return result.data;
      }
      
      // Group images by style category (standard, vectorial, collage, person) for diverse selection
      const imagesByStyle = {};
      result.data.forEach(image => {
        // Extract style from filename (e.g., "_standard_", "_vectorial_", "_collage_", "_person_")
        const styleMatch = image.url.match(/_([^_]+)_\d+\.(png|jpg|jpeg)$/i);
        if (styleMatch) {
          const style = styleMatch[1].toLowerCase();
          if (!imagesByStyle[style]) {
            imagesByStyle[style] = [];
          }
          imagesByStyle[style].push(image);
        }
      });
      
      console.log('Images grouped by style:', Object.keys(imagesByStyle).map(style => `${style}: ${imagesByStyle[style].length}`));
      
      // Select one image from each different style category, up to 4 total (for Quick Launch)
      const selectedImages = [];
      const preferredStyles = ['standard', 'person', 'vectorial', 'collage']; // Priority order
      
      // First, try to get one from each different style (aim for 4 diverse styles)
      for (const style of preferredStyles) {
        if (imagesByStyle[style] && imagesByStyle[style].length > 0 && selectedImages.length < 4) {
          // Pick a random image from this style category
          const randomIndex = Math.floor(Math.random() * imagesByStyle[style].length);
          selectedImages.push(imagesByStyle[style][randomIndex]);
          console.log(`Selected ${style} style image for variation ${selectedImages.length}`);
        }
      }
      
      // If we still need more images, fill from any available styles
      if (selectedImages.length < 4) {
        for (const style of Object.keys(imagesByStyle)) {
          const availableImages = imagesByStyle[style].filter(img => !selectedImages.includes(img));
          while (availableImages.length > 0 && selectedImages.length < 4) {
            const randomIndex = Math.floor(Math.random() * availableImages.length);
            selectedImages.push(availableImages[randomIndex]);
            availableImages.splice(randomIndex, 1);
            console.log(`Added additional ${style} style image for variation ${selectedImages.length}`);
          }
        }
      }
      
      // Fallback to original images if pattern matching failed
      if (selectedImages.length === 0 && result.data.length > 0) {
        console.warn('Could not parse image styles, using first 4 images as fallback');
        return result.data.slice(0, 4);
      }
      
      console.log(`Returning ${selectedImages.length} diverse style images`);
      return selectedImages;
    } else {
      console.warn('API returned success but no images were found');
      return [];
    }
  } catch (error) {
    console.error('Error fetching preloaded images:', error);
    // Continue without preloaded images instead of breaking the flow
    return [];
  }
};

/**
 * Edit an existing image using OpenAI's Image Edits API
 * @param {string} imageUrl URL of the image to edit
 * @param {string} prompt Description of desired changes
 * @param {string} maskUrl Optional mask image URL
 * @param {string} size Image size (default: 1024x1024)
 * @param {number} n Number of variations to generate (default: 1)
 * @param {string} model Model to use (default: gpt-image-1-mini)
 * @returns {Promise<Object>} Edited image data
 */
export const editImageWithOpenAI = async (imageUrl, prompt, maskUrl = null, size = '1024x1024', n = 1, model = 'gpt-image-1-mini') => {
  try {
    console.log(`Editing image with OpenAI using ${model}: ${prompt}`);
    
    const response = await Api.editImageWithOpenAI({
      image_url: imageUrl,
      prompt: prompt,
      mask_url: maskUrl,
      size: size,
      n: n,
      model: model
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to edit image');
    }
    
    console.log('Image edit successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error editing image with OpenAI:', error);
    throw error;
  }
};

/**
 * Generate multiple ad variations
 * @param {Object} page RSOC page data
 * @param {string} objective Campaign objective
 * @param {number} count Number of variations to generate
 * @param {boolean} usePreloadedImages Whether to use preloaded images if available
 * @returns {Promise<Array>} Array of ad variations
 */
export const generateAdVariations = async (page, objective, count = 3, usePreloadedImages = true) => {
  if (!page || !page.title) {
    throw new Error('Invalid page data. Page title is required.');
  }

  console.log('Starting ad variations generation with params:', {
    title: page.title,
    slug: page.slug,
    objective,
    count,
    usePreloadedImages
  });

  const formattedPage = formatPageForAI(page);
  const variations = [];
  let preloadedImages = [];
  
  // Try to fetch preloaded images if enabled
  if (usePreloadedImages && formattedPage.slug) {
    console.log(`Will attempt to use preloaded images for slug: ${formattedPage.slug}`);
    preloadedImages = await fetchPreloadedImages(formattedPage.slug);
  } else {
    console.log('Not using preloaded images due to:', 
      !usePreloadedImages ? 'feature disabled' : 'missing slug');
  }
  
  // If we have preloaded images, use those first
  const havePreloadedImages = preloadedImages.length > 0;
  console.log(`Preloaded images available: ${havePreloadedImages ? 'YES' : 'NO'}, count: ${preloadedImages.length}`);

  for (let i = 0; i < count; i++) {
    try {
      console.log(`Generating variation ${i + 1} of ${count}`);
      
      // Generate copy for this variation
      const copy = await generateAdCopy({
        page: formattedPage,
        objective,
        variationIndex: i + 1,
      });

      // Use preloaded image if available, otherwise generate new one
      let image;
      if (havePreloadedImages && i < preloadedImages.length) {
        // Use a preloaded image
        image = preloadedImages[i];
        console.log(`Using preloaded image for variation ${i + 1}: ${image.url}`);
      } else {
        // Generate new image
        console.log(`No preloaded image available for variation ${i + 1}, generating new one...`);
        image = await generateAdImage({
          page: formattedPage,
          style: 'professional', // Can be customized based on page content
        });
        console.log(`New image generated: ${image.url}`);
      }

      variations.push({
        copy,
        image,
        variationId: `var_${i + 1}`,
      });
    } catch (error) {
      console.error(`Error generating variation ${i + 1}:`, error);
      // Continue with other variations if one fails
    }
  }

  console.log(`Generated ${variations.length} variations`);
  return variations;
};

/**
 * Validate generated content against Facebook's ad policies
 * @param {Object} adContent The generated ad content
 * @returns {Object} Validation results
 */
export const validateAdContent = (adContent) => {
  const issues = [];
  const recommendations = [];

  // Check text length limits
  if (adContent.copy.headline && adContent.copy.headline.length > 40) {
    recommendations.push('Headline exceeds 40 characters recommendation');
  }
  if (adContent.copy.primaryText && adContent.copy.primaryText.length > 125) {
    issues.push('Primary text exceeds 125 characters limit');
  }
  
  // Description length is also a recommendation
  if (adContent.copy.description && adContent.copy.description.length > 25) {
    recommendations.push('Description exceeds 25 characters recommendation');
  }

  // Check for prohibited content (compliance checks)
  const prohibitedTerms = [
    'guarantee',
    'guaranteed',
    'before and after',
    'miracle',
    'click here',
    'search now',
    'best offer',
    'free cash',
    'free money',
    'we\'re hiring',
    'no credit check',
    '$1000 car',
    'admission guaranteed',
    'loan approved',
  ];

  const allText = `${adContent.copy.headline || ''} ${adContent.copy.primaryText || ''} ${adContent.copy.description || ''}`.toLowerCase();

  prohibitedTerms.forEach(term => {
    if (allText.includes(term.toLowerCase())) {
      issues.push(`Ad contains prohibited term: ${term}`);
    }
  });

  return {
    isValid: issues.length === 0,
    issues,
    recommendations
  };
};

export default {
  generateAdCopy,
  generateAdImage,
  generateAdVariations,
  formatPageForAI,
  validateAdContent,
  fetchPreloadedImages,
}; 